import {
  AdvancedBloomFilter,
  GlowFilter,
  GodrayFilter,
  MotionBlurFilter,
  RGBSplitFilter,
  ShockwaveFilter,
} from 'pixi-filters';
import { gsap } from 'gsap';
import type { Container, Filter } from 'pixi.js';
import { DESIGN, type QualityProfile } from '../core/runtime.js';

/**
 * The post chain.
 *
 * Order matters and is fixed: shockwave (geometry) -> chromatic split -> god
 * rays -> motion blur -> bloom. Bloom is last so it lifts everything below it,
 * which is what makes the neon read as light rather than as a coloured outline.
 *
 * Every stage is optional and is simply absent on lower quality tiers - the
 * filter array is rebuilt, never left in place with a zero-strength pass, so a
 * weak encoder genuinely pays nothing for effects it cannot afford.
 */
export class FilterStack {
  private readonly bloom: AdvancedBloomFilter | null;
  private readonly motion: MotionBlurFilter | null;
  private readonly godray: GodrayFilter | null;
  private readonly split: RGBSplitFilter | null;
  private readonly shockwave: ShockwaveFilter | null;

  private godrayActive = false;
  private shockwaveActive = false;
  private elapsed = 0;

  constructor(
    private readonly world: Container,
    quality: QualityProfile,
  ) {
    this.bloom = quality.bloom ? createBloom() : null;
    this.motion = quality.motionBlur ? createMotionBlur() : null;
    this.godray = quality.godRays ? createGodray() : null;
    this.split = quality.chromaticAberration ? createSplit() : null;
    this.shockwave = quality.bloom ? createShockwave() : null;

    this.rebuild();
  }

  private rebuild(): void {
    const filters: Filter[] = [];
    if (this.shockwave && this.shockwaveActive) filters.push(this.shockwave);
    if (this.split) filters.push(this.split);
    if (this.godray && this.godrayActive) filters.push(this.godray);
    if (this.motion) filters.push(this.motion);
    if (this.bloom) filters.push(this.bloom);
    this.world.filters = filters;
  }

  update(deltaSeconds: number, cameraSpeed: number): void {
    this.elapsed += deltaSeconds;

    if (this.godray && this.godrayActive) {
      this.godray.time += deltaSeconds * 0.42;
    }

    if (this.motion) {
      // Blur follows the camera, capped so a hard cut does not smear to mush.
      const magnitude = Math.min(cameraSpeed / 90, 9);
      this.motion.velocity = { x: magnitude, y: magnitude * 0.35 };
    }

    if (this.shockwave && this.shockwaveActive) {
      this.shockwave.time += deltaSeconds;
      if (this.shockwave.time > 1.3) {
        this.shockwaveActive = false;
        this.rebuild();
      }
    }
  }

  /** Expanding ring from a world point. Used on critical strikes and knockouts. */
  shock(x: number, y: number, amplitude = 34): void {
    if (!this.shockwave) return;
    this.shockwave.center = { x, y };
    this.shockwave.amplitude = amplitude;
    this.shockwave.time = 0;
    if (!this.shockwaveActive) {
      this.shockwaveActive = true;
      this.rebuild();
    }
  }

  /** Brief chromatic tear. Sparingly - it reads as damage, not as decoration. */
  glitch(strength = 9, duration = 0.32): void {
    const split = this.split;
    if (!split) return;

    const proxy = { value: strength };
    gsap.to(proxy, {
      value: 0,
      duration,
      ease: 'power2.out',
      onUpdate: () => {
        split.red = { x: -proxy.value, y: 0 };
        split.green = { x: 0, y: proxy.value * 0.4 };
        split.blue = { x: proxy.value, y: 0 };
      },
      onComplete: () => {
        split.red = { x: 0, y: 0 };
        split.green = { x: 0, y: 0 };
        split.blue = { x: 0, y: 0 };
      },
    });
  }

  /** Volumetric light for ceremonies. */
  setGodRays(active: boolean, alpha = 0.42): void {
    if (!this.godray) return;
    if (active === this.godrayActive) {
      if (active) gsap.to(this.godray, { alpha, duration: 1.2 });
      return;
    }

    this.godrayActive = active;
    this.rebuild();
    gsap.to(this.godray, { alpha: active ? alpha : 0, duration: 1.4, ease: 'sine.inOut' });
  }

  /** Pump the bloom on a big moment, then settle back. */
  flashBloom(scale = 2.1, duration = 0.9): void {
    const bloom = this.bloom;
    if (!bloom) return;
    gsap.killTweensOf(bloom);
    gsap.fromTo(
      bloom,
      { bloomScale: scale, brightness: 1.16 },
      { bloomScale: 1.05, brightness: 1.0, duration, ease: 'power2.out' },
    );
  }
}

function createBloom(): AdvancedBloomFilter {
  const bloom = new AdvancedBloomFilter();
  bloom.threshold = 0.42;
  bloom.bloomScale = 1.05;
  bloom.brightness = 1;
  bloom.blur = 7;
  bloom.quality = 4;
  return bloom;
}

function createMotionBlur(): MotionBlurFilter {
  const motion = new MotionBlurFilter();
  motion.velocity = { x: 0, y: 0 };
  motion.kernelSize = 7;
  motion.offset = 0;
  return motion;
}

function createGodray(): GodrayFilter {
  const godray = new GodrayFilter();
  godray.angle = 28;
  godray.gain = 0.42;
  godray.lacunarity = 2.4;
  godray.alpha = 0;
  godray.parallel = true;
  godray.time = 0;
  return godray;
}

function createSplit(): RGBSplitFilter {
  const split = new RGBSplitFilter();
  split.red = { x: 0, y: 0 };
  split.green = { x: 0, y: 0 };
  split.blue = { x: 0, y: 0 };
  return split;
}

function createShockwave(): ShockwaveFilter {
  const shockwave = new ShockwaveFilter();
  shockwave.center = { x: DESIGN.width / 2, y: DESIGN.height / 2 };
  shockwave.amplitude = 30;
  shockwave.wavelength = 190;
  shockwave.speed = 620;
  shockwave.brightness = 1.08;
  shockwave.radius = -1;
  shockwave.time = 5;
  return shockwave;
}

/** Rim light for a highlighted card. Cheap enough to leave on a few objects. */
export function createGlow(color: number, strength = 2.2, distance = 20): GlowFilter {
  return new GlowFilter({
    distance,
    outerStrength: strength,
    innerStrength: 0,
    color,
    quality: 0.35,
    alpha: 1,
  });
}
